import {
	LOCALIZED_REFERRAL_PAYOUT_SCHEDULE,
	UserAction,
} from "@brookbush/types";

import { useAuth } from "@clerk/nextjs";
import clsx from "clsx";
import { motion, useMotionValueEvent, useScroll } from "framer-motion";
import {
	Award,
	BadgeCheckIcon,
	BookCheck,
	BookOpen,
	BuildingIcon,
	Clapperboard,
	FileText,
	GlobeIcon,
	HandshakeIcon,
	HelpCircle,
	LibraryBigIcon,
	LifeBuoyIcon,
	type LucideIcon,
	MessageCircleQuestionIcon,
	PickaxeIcon,
	RadioTowerIcon,
	Video,
	WaypointsIcon,
} from "lucide-react";
import { useRouter } from "next/compat/router";
import Image from "next/image";
import Link from "next/link";
import posthog from "posthog-js";
import { useMemo, useState } from "react";

import AnnotationsButton from "./Annotations/AnnotationsButton";
import Breadcrumbs, { type BreadcrumbItem } from "./Breadcrumbs";
import NavbarLink from "./NavbarLink";
import NavbarSnackbar from "./NavbarSnackbar";
import NavbarUserMenu from "./NavbarUserMenu";
import SearchButton from "./SearchButton";
import Skeleton from "./Skeleton";
import { useAccess, useUserAction } from "~/hooks";
import useSale from "~/hooks/useSale";
import useTrackCTA from "~/hooks/useTrackCTA";
import useUserGeo from "~/hooks/useUserGeo";
import { formatPrice } from "~/lib/stripe/helpers";

interface NavbarProps {
	isHomePage?: boolean;
	breadcrumbs?: BreadcrumbItem[];
	className?: string;
}

export interface INavbarLink {
	icon: LucideIcon;
	label: string;
	href?: string;

	className?: string;
	isCatalogued?: boolean;
	target?: string;
	onClick?: () => void;
	children?: {
		href?: string;
		label: string;
		target?: string;
		className?: string;
		icon: LucideIcon;
		description?: string;
		abbreviation?: string;
		intercomButton?: boolean;
	}[];
}

const Navbar = ({ isHomePage, breadcrumbs, className }: NavbarProps) => {
	const router = useRouter();
	const { isSignedIn, isLoaded } = useAuth();
	const { isMember, isPaused, isLoadingAccess } = useAccess();

	const { userGeo, isLoadingUserGeo } = useUserGeo();

	const links: INavbarLink[] = [
		{
			icon: Award,
			label: "Certifications",

			children: [
				{
					href: "/certifications/certified-personal-trainer",
					label: "Certified Personal Trainer",
					icon: Award,
					abbreviation: "CPT",
					description: "Strength and Performance",
				},
				{
					href: "/certifications/cpt-certification-study-plan",
					label: "CPT Certification Study Plan",
					icon: Award,
					abbreviation: "CPT Study Plan",
					description: "Exam Preparation",
				},
				{
					href: "/certifications/strength-and-performance-coach",
					label: "Strength and Performance Coach",
					icon: Award,
					abbreviation: "SPC",
					description: "Strength and Performance",
				},

				{
					href: "/certifications/human-movement-specialist",
					label: "Human Movement Specialist",
					icon: Award,
					description: "Corrective Exercise",
					abbreviation: "HMS",
				},

				{
					href: "/certifications/integrated-manual-therapist",
					label: "Integrated Manual Therapist",
					icon: Award,
					description: "Manual Therapy",
					abbreviation: "IMT",
				},

				{
					href: "/exam-portal",
					label: "CPT Exam Portal",
					icon: BookCheck,
					description: "CPT Exam Portal",
					abbreviation: "",
				},
			],
		},

		{
			icon: LibraryBigIcon,
			label: "Content",

			children: [
				{
					href: "/courses",
					label: "Courses",
					description: "200+ Courses For Continuing Education",
					icon: BookOpen,
				},
				{
					href: "/videos",
					label: "Videos",
					description: "500+ Videos For Practical Learning",
					icon: Video,
				},

				{
					href: "/articles",
					label: "Articles",
					icon: FileText,
					description: "500+ Articles For Expert Insights",
				},

				{
					href: "/glossary",
					label: "Glossary",
					icon: HelpCircle,
					description: "Definition and Terms",
				},
			],
		},

		{
			href: isSignedIn ? "/programs" : "/info/programs",
			label: "Programs",
			icon: WaypointsIcon,
			className: "phablet:hidden xl:block",
		},

		{
			label: "Live Events",
			icon: RadioTowerIcon,
			children: [
				{
					href: "/live-events/webinars",
					label: "Webinars",
					icon: Clapperboard,
					description: "Live Expert Sessions",
				},
				{
					href: "/live-events/workshops",
					label: "Workshops",
					icon: PickaxeIcon,
					description: "Interactive Skill Training",
				},
			],
		},

		{
			label: "Resources",

			icon: BuildingIcon,
			children: [
				{
					href: "/about",
					label: "About us",
					icon: BuildingIcon,
				},

				{
					href: "/about#partners",
					label: "Partners",
					icon: HandshakeIcon,
				},
				{
					href: "/about#accreditations",
					label: "Accreditations",
					icon: BadgeCheckIcon,
				},

				{
					label: "Help Center",
					icon: LifeBuoyIcon,
					target: "_blank",
					href: "https://help.brookbushinstitute.com",
				},
				{
					intercomButton: true,
					label: "Chat with Support",
					icon: MessageCircleQuestionIcon,
				},

				{
					href: "/professional-locator",
					label: "Professional Locator",
					className: "phablet:hidden xl:block",
					icon: GlobeIcon,
				},
			],
		},
	];

	const { scrollY } = useScroll();
	const [isScrolling, setIsScrolling] = useState(false);

	const { setUserAction } = useUserAction();

	const trackCTA = useTrackCTA();

	const referralAmount = useMemo(
		() =>
			formatPrice({
				currency: "usd",
				value:
					LOCALIZED_REFERRAL_PAYOUT_SCHEDULE[userGeo?.currency ?? "usd"]
						?.total / 100,
			}),
		[userGeo?.currency],
	);

	useMotionValueEvent(scrollY, "change", (y) => {
		setIsScrolling(y > 0);
	});

	const { sale, isLoadingSale } = useSale();

	const isLoadingNavbar =
		!isLoaded || isLoadingSale || isLoadingUserGeo || isLoadingAccess;

	const isInverted = isHomePage && !isScrolling;

	return (
		<div className={clsx("sticky top-0 z-20")}>
			<motion.header
				className={clsx(
					"flex h-16 items-center phablet:justify-between gap-2 border-transparent border-b bg-white px-4 py-2 shadow-transparent shadow-xl transition-all duration-300 dark:bg-slate-800",
					{
						"border-inherit!": router?.asPath !== "/",
					},
					{
						"border-inherit! shadow-black/3!": isScrolling,
					},
					{
						"border-b-transparent! bg-transparent!": isInverted,
					},
					className,
				)}
			>
				<div className="phablet:hidden">
					<NavbarSnackbar darkIcon={isInverted} links={links} />
				</div>

				<Link prefetch={false} href="/" className={clsx("laptop:block hidden")}>
					<div className="relative h-10 w-64">
						<Image
							priority
							fetchPriority="high"
							src={`${process.env.NEXT_PUBLIC_BLOB_STORAGE_URL}/brookbush-institute-logo-dark.webp`}
							alt="Brookbush Institute Logo Dark"
							fill
							sizes="(min-width: 1024px) 360px, (min-width: 640px) 160px, 140px"
							className={clsx("hidden object-contain dark:block", {
								"!block": isInverted,
							})}
						/>

						<Image
							priority
							fetchPriority="high"
							src={`${process.env.NEXT_PUBLIC_BLOB_STORAGE_URL}/brookbush-institute-logo.webp`}
							alt="Brookbush Institute Logo"
							fill
							sizes="(min-width: 1024px) 360px, (min-width: 640px) 160px, 140px"
							className={clsx("object-contain dark:hidden", {
								"!hidden": isInverted,
							})}
						/>
					</div>
				</Link>

				<Link href="/" prefetch={false} className="laptop:hidden">
					<div className={clsx("relative aspect-square h-9")}>
						<Image
							priority
							fetchPriority="high"
							sizes="60px"
							src={`${process.env.NEXT_PUBLIC_BLOB_STORAGE_URL}/BI-logo-primary-small`}
							alt="Brookbush Institute Logo"
							fill
							className={clsx("object-contain dark:brightness-125", {
								"!brightness-125": isInverted,
							})}
						/>
					</div>
				</Link>

				<div
					className={clsx("relative phablet:flex hidden items-center gap-1")}
				>
					<nav className={clsx("flex tablet:gap-1")}>
						{links.map((link) => (
							<NavbarLink
								link={link}
								key={link.label}
								isInverted={isInverted}
							/>
						))}
					</nav>
				</div>

				<div className={clsx("ml-auto phablet:ml-0 flex justify-end")}>
					{isLoadingNavbar ? (
						<div className="flex h-full items-center">
							<div className="phablet:block hidden">
								<SearchButton />
							</div>

							<Skeleton className="ml-3 h-[44px] phablet:w-[193.06px] w-[102.77px]" />
							<Skeleton className="-mr-1 ml-2 h-[44px] phablet:w-[86.3px] w-[79.83px]" />
						</div>
					) : !isSignedIn ? (
						<div className="flex items-center">
							<SearchButton isInverted={isInverted} />

							<div className="ml-2 tablet:ml-3 flex gap-1.5 phablet:gap-2">
								<button
									onClick={() => {
										trackCTA(UserAction.SIGN_UP, "Navbar Button");
										posthog.capture("cta:signup_navbar_clicked");

										setUserAction(UserAction.SIGN_UP);
									}}
									className={clsx(
										"group flex items-center gap-1 whitespace-nowrap rounded-[9px] bg-gradient-to-r from-sky-400 to-sky-600 phablet:px-3 px-2.5 tablet:px-4 py-2 font-medium phablet:text-lg text-white shadow-lg shadow-sky-600/20 transition-all hover:brightness-105 dark:from-sky-600 dark:to-sky-800 dark:shadow-slate-900",
										{
											"from-sky-600 to-sky-800 shadow-slate-900": isInverted,
										},
									)}
								>
									Sign Up
								</button>

								<button
									onClick={() => setUserAction(UserAction.SIGN_IN)}
									className={clsx(
										"group -mr-1 flex items-center gap-1 whitespace-nowrap rounded-[9px] border-2 border-sky-600 phablet:px-3 px-2.5 py-1.5 font-medium phablet:text-lg shadow-lg shadow-slate-200/50 hover:bg-slate-50 dark:shadow-slate-900/80 dark:hover:bg-slate-700",
										{
											"!shadow-black/20 bg-slate-800/60 text-white hover:bg-slate-800 dark:hover:bg-slate-800":
												isInverted,
										},
									)}
								>
									Sign In
								</button>
							</div>
						</div>
					) : (
						<div className="flex items-center">
							{isLoadingAccess ? null : isMember && !sale?.isAnnualUpgrade ? (
								<Link
									href="/referrals"
									className="mr-1 tablet:mr-2 tablet:block hidden whitespace-nowrap rounded-full border-2 border-sky-500 phablet:px-3.5 px-2.5 tablet:px-3.5 phablet:py-1 py-1 tablet:py-1 font-medium transition-all hover:bg-sky-500 hover:text-white dark:border-sky-600 dark:hover:bg-sky-600"
								>
									<span>Refer & Earn {referralAmount}</span>
								</Link>
							) : sale ? (
								<button
									onClick={() => {
										trackCTA(UserAction.BECOME_A_MEMBER, "Navbar Button");
										posthog.capture("cta:membership_navbar_clicked");

										setUserAction(UserAction.BECOME_A_MEMBER);
									}}
									className="animated-background mr-1 tablet:mr-2 tablet:block hidden whitespace-nowrap rounded-full bg-gradient-to-r from-yellow-900 via-sky-900 to-yellow-900 tablet:px-3.5 tablet:py-1.5 font-medium text-white shadow-black/20 shadow-xl transition-all hover:brightness-125"
								>
									{sale?.buttonText}
								</button>
							) : (
								<button
									onClick={() => {
										trackCTA(UserAction.BECOME_A_MEMBER, "Navbar Button");
										posthog.capture("cta:membership_navbar_clicked");
										setUserAction(UserAction.BECOME_A_MEMBER);
									}}
									className="mr-1 tablet:mr-2 whitespace-nowrap rounded-full bg-sky-500 phablet:px-3.5 px-3 tablet:px-3.5 phablet:py-1 py-1.5 tablet:py-1.5 font-medium text-white shadow-lg transition-all hover:bg-sky-600 min-[390px]:mr-2.5 dark:bg-sky-600 dark:hover:bg-sky-500"
								>
									{isPaused ? (
										<>
											<span className="hidden lg:block">Resume Membership</span>

											<span className="lg:hidden">Resume</span>
										</>
									) : (
										<>
											<span className="phablet:hidden">Join Today</span>
											<span className="phablet:inline-block hidden">
												Become a Member
											</span>
										</>
									)}
								</button>
							)}
							<SearchButton />

							<AnnotationsButton />
							<NavbarUserMenu />
						</div>
					)}
				</div>
			</motion.header>
			{!!breadcrumbs?.length && (
				<Breadcrumbs
					className={
						isScrolling
							? "!border-inherit !shadow-black/[3%] dark:!shadow-black/[20%] !phablet:px-5"
							: "!phablet:px-5"
					}
					items={breadcrumbs}
				/>
			)}
		</div>
	);
};

export default Navbar;
